import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { formatPersianDate } from '../../../utils/formatPersianDate';
import { formatPersianNumber } from '../../../utils/formatPersianNumber';
import { formatStr } from '../../../utils/formatStr';
import { createRefund } from '../../../api/followup';

const STATUS_MAP = {
  progress: {
    label: 'در انتظار ارسال',
    color: '#f59e0b',
    bg: 'from-amber-50 to-yellow-50',
    border: 'border-amber-200',
  },
  done: {
    label: 'تحویل داده شده',
    color: '#22c55e',
    bg: 'from-green-50 to-emerald-50',
    border: 'border-green-200',
  },
  failed: {
    label: 'ارسال ناموفق',
    color: '#ef4444',
    bg: 'from-red-50 to-rose-50',
    border: 'border-red-200',
  },
};

const STATUS_ICON_MAP = {
  progress: 'Clock',
  done: 'CheckCircle',
  failed: 'AlertCircle',
};

const REFUND_STATUS_MAP = {
  new: { label: 'در انتظار بررسی', color: '#f59e0b' },
  done: { label: 'انجام شده', color: '#22c55e' },
  failed: { label: 'ناموفق', color: '#ef4444' },
};

const FollowupStatusCard = ({
  transaction,
  msgReq,
  refundReq,
  refundable,
  tid,
}) => {
  const navigate = useNavigate();
  const statusInfo = STATUS_MAP[msgReq?.status] || STATUS_MAP.progress;

  // Refund form state
  const [cardNumber, setCardNumber] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState(null);
  const [createdRefund, setCreatedRefund] = useState(null);

  // Existing refund from API
  const existingRefund = refundReq || createdRefund;
  const refundInfo = existingRefund
    ? REFUND_STATUS_MAP[existingRefund.status] || REFUND_STATUS_MAP.new
    : null;

  const formattedMessage = msgReq?.msg
    ? formatStr(msgReq.msg, msgReq?.receiver_name || '')
    : '---';

  const handleRefundSubmit = async (e) => {
    e.preventDefault();
    const trimmed = cardNumber.trim();

    if (!trimmed) {
      setRefundError('لطفاً شماره کارت را وارد کنید');
      return;
    }

    if (!/^\d{16}$/.test(trimmed)) {
      setRefundError('شماره کارت باید ۱۶ رقم باشد');
      return;
    }

    try {
      setRefundSubmitting(true);
      setRefundError(null);
      const result = await createRefund(transaction?.id, trimmed.toString());
      setCreatedRefund(result);
    } catch (err) {
      setRefundError(
        'ثبت درخواست بازگشت وجه با مشکل مواجه شد. لطفاً دوباره تلاش کنید.',
      );
      console.error('Refund creation failed:', err);
    } finally {
      setRefundSubmitting(false);
    }
  };

  const showRefundForm =
    msgReq?.status === 'failed' && refundable && !existingRefund;

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center bg-gradient-to-r from-purple-500 to-pink-500 rounded-full p-4 shadow-lg">
          <Icon name="ClipboardList" size={36} color="#ffffff" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
          وضعیت پیام
        </h1>
      </div>

      {/* Message Content Card */}
      <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-4 md:space-y-5">
        <h2 className="text-lg md:text-xl font-bold text-gray-800 border-b border-purple-100 pb-3">
          متن پیام
        </h2>

        {/* Message text */}
        <div className="flex items-start gap-3 md:gap-4">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-2 md:p-3 flex-shrink-0">
            <Icon name="MessageSquare" size={20} color="#ffffff" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm md:text-base text-gray-500 mb-1 md:mb-2">
              متن پیام
            </p>
            <p className="text-base md:text-lg lg:text-xl font-semibold text-gray-800 leading-relaxed break-words whitespace-pre-line">
              {formattedMessage}
            </p>
          </div>
        </div>

        {/* Receiver name */}
        <div className="flex items-start gap-3 md:gap-4">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-2 md:p-3 flex-shrink-0">
            <Icon name="User" size={20} color="#ffffff" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm md:text-base text-gray-500 mb-1 md:mb-2">
              نام گیرنده
            </p>
            <p className="text-base md:text-lg lg:text-xl font-semibold text-gray-800">
              {msgReq?.receiver_name || '---'}
            </p>
          </div>
        </div>

        {/* Phone number */}
        <div className="flex items-start gap-3 md:gap-4">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-2 md:p-3 flex-shrink-0">
            <Icon name="Phone" size={20} color="#ffffff" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm md:text-base text-gray-500 mb-1 md:mb-2">
              شماره تلفن
            </p>
            <p className="text-base md:text-lg lg:text-xl font-semibold text-gray-800">
              {msgReq?.phone_number
                ? formatPersianNumber(msgReq.phone_number)
                : '---'}
            </p>
          </div>
        </div>

        {/* Send time */}
        <div className="flex items-start gap-3 md:gap-4">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-2 md:p-3 flex-shrink-0">
            <Icon name="Clock" size={20} color="#ffffff" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm md:text-base text-gray-500 mb-1 md:mb-2">
              زمان ارسال
            </p>
            <p className="text-base md:text-lg lg:text-xl font-semibold text-gray-800">
              {msgReq?.sent_at ? formatPersianDate(msgReq.sent_at) : '---'}
            </p>
          </div>
        </div>
      </div>

      {/* Transaction Details Card */}
      <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-3 md:space-y-4">
        <h2 className="text-lg md:text-xl font-bold text-gray-800 border-b border-purple-100 pb-3">
          جزئیات تراکنش
        </h2>

        {/* Amount */}
        <div className="flex justify-between items-center pb-3 border-b border-purple-100">
          <span className="text-sm md:text-base text-gray-500">
            مبلغ تراکنش
          </span>
          <span className="text-sm md:text-base lg:text-lg font-bold text-purple-600">
            {transaction?.amount != null
              ? `${formatPersianNumber(transaction.amount)} تومان`
              : '---'}
          </span>
        </div>

        {/* Tracking code */}
        <div className="flex justify-between items-center pb-3 border-b border-purple-100">
          <span className="text-sm md:text-base text-gray-500">کد پیگیری</span>
          <span
            className="text-sm md:text-base lg:text-lg font-bold text-gray-800"
            dir="ltr"
          >
            {transaction?.id != null ? transaction.id : '---'}
          </span>
        </div>

        {/* Date */}
        <div className="flex justify-between items-center pb-3 border-b border-purple-100">
          <span className="text-sm md:text-base text-gray-500">تاریخ</span>
          <span className="text-sm md:text-base lg:text-lg font-bold text-gray-800">
            {transaction?.updated_at
              ? formatPersianDate(transaction.updated_at)
              : '---'}
          </span>
        </div>
      </div>

      {/* Delivery Status Badge Card */}
      <div
        className={`bg-gradient-to-r ${statusInfo.bg} border-2 ${statusInfo.border} rounded-2xl p-4 md:p-6 lg:p-8`}
      >
        <div className="flex items-start gap-3 md:gap-4">
          <div className="flex-shrink-0 mt-1">
            <Icon
              name={STATUS_ICON_MAP[msgReq?.status] || 'Clock'}
              size={20}
              color={statusInfo.color}
              className="md:w-6 md:h-6"
            />
          </div>
          <div className="flex-1 min-w-0 space-y-2 md:space-y-3">
            <div className="flex items-center gap-3">
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-800">
                وضعیت ارسال پیام
              </h3>
              <span
                className="inline-block px-3 py-1 rounded-full text-xs md:text-sm font-semibold text-white"
                style={{ backgroundColor: statusInfo.color }}
              >
                {statusInfo.label}
              </span>
            </div>
            {msgReq?.sent_at && (
              <p className="text-sm text-gray-500">
                زمان ارسال: {formatPersianDate(msgReq.sent_at)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Existing Refund Info Block */}
      {existingRefund && !showRefundForm && (
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-4 border-r-4 border-amber-400">
          <div className="flex items-center gap-3 pb-3 border-b border-amber-100">
            <Icon name="RotateCcw" size={20} color="#f59e0b" />
            <h3 className="text-lg md:text-xl font-bold text-gray-800">
              درخواست بازگشت وجه
            </h3>
          </div>

          <div className="flex justify-between items-center pb-3 border-b border-amber-50">
            <span className="text-sm md:text-base text-gray-500">وضعیت</span>
            <span
              className="inline-block px-3 py-1 rounded-full text-xs md:text-sm font-semibold text-white"
              style={{ backgroundColor: refundInfo?.color || '#f59e0b' }}
            >
              {refundInfo?.label || 'نامشخص'}
            </span>
          </div>

          {existingRefund.card_number && (
            <div className="flex justify-between items-center pb-3 border-b border-amber-50">
              <span className="text-sm md:text-base text-gray-500">
                شماره کارت
              </span>
              <span
                className="text-sm md:text-base font-semibold text-gray-800"
                dir="ltr"
              >
                {formatPersianNumber(existingRefund.card_number)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Refund Request Form (status=failed, refundable=true, no existing refund) */}
      {showRefundForm && (
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-4 border-r-4 border-red-400">
          <div className="flex items-center gap-3 pb-3 border-b border-red-100">
            <Icon name="RotateCcw" size={20} color="#ef4444" />
            <h3 className="text-lg md:text-xl font-bold text-gray-800">
              درخواست بازگشت وجه
            </h3>
          </div>

          <p className="text-sm md:text-base text-gray-600 leading-relaxed">
            با توجه به عدم موفقیت در ارسال پیام، می‌توانید با وارد کردن شماره
            کارت خود درخواست بازگشت وجه دهید.
          </p>

          {!refundSubmitting && !createdRefund && (
            <form onSubmit={handleRefundSubmit} className="space-y-4">
              <div className="text-right">
                <label
                  htmlFor="card-number"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  شماره کارت
                </label>
                <input
                  id="card-number"
                  type="text"
                  inputMode="numeric"
                  maxLength={16}
                  value={cardNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 16);
                    setCardNumber(val);
                    setRefundError(null);
                  }}
                  placeholder="شماره ۱۶ رقمی کارت"
                  dir="ltr"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center text-lg
                             tracking-widest
                             focus:ring-2 focus:ring-purple-500 focus:border-purple-500
                             outline-none transition-all"
                />
                {refundError && (
                  <p className="text-sm text-red-500 mt-1">{refundError}</p>
                )}
              </div>

              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 w-full px-8 py-3
                           bg-gradient-to-r from-red-600 to-rose-600
                           hover:from-red-700 hover:to-rose-700
                           text-white rounded-xl font-semibold text-lg transition-all"
              >
                <Icon name="Send" size={18} color="#ffffff" />
                ثبت درخواست بازگشت وجه
              </button>
            </form>
          )}

          {refundSubmitting && (
            <div className="flex flex-col items-center justify-center py-6 space-y-3">
              <div className="h-8 w-8 rounded-full border-3 border-t-transparent animate-spin border-red-500" />
              <p className="text-sm text-gray-500">در حال ثبت درخواست...</p>
            </div>
          )}

          {createdRefund && !refundSubmitting && (
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-3 border-b border-green-100">
                <span className="text-sm md:text-base text-gray-500">
                  وضعیت
                </span>
                <span className="inline-block px-3 py-1 rounded-full text-xs md:text-sm font-semibold text-white bg-green-500">
                  ثبت شد
                </span>
              </div>
              {createdRefund.card_number && (
                <div className="flex justify-between items-center pb-3 border-b border-green-100">
                  <span className="text-sm md:text-base text-gray-500">
                    شماره کارت
                  </span>
                  <span
                    className="text-sm md:text-base font-semibold text-gray-800"
                    dir="ltr"
                  >
                    {formatPersianNumber(createdRefund.card_number)}
                  </span>
                </div>
              )}
              <p className="text-sm text-green-600 text-center">
                درخواست بازگشت وجه شما با موفقیت ثبت شد.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Info Card (conditional, if status !== 'done') */}
      {msgReq?.status !== 'done' && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 md:p-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Icon name="Info" size={20} color="#3b82f6" />
            </div>
            <p className="text-sm md:text-base text-blue-800 leading-relaxed">
              در صورتی که می‌خواهید بعدا پیام را پیگیری کنید کد پیگیری را نزد
              خود نگه داشته و از قسمت پیگیری پیام(لینک‌های مهم) اقدام نمایید.
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons Card */}
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-4 md:space-y-6">
        <div className="space-y-2 md:space-y-3">
          <h3 className="text-lg md:text-2xl font-bold text-gray-800">
            آیا می‌خواهید پیام دیگری ارسال کنید؟
          </h3>
          <p className="text-sm md:text-base text-gray-600 max-w-xl mx-auto">
            می‌توانید به صفحه اصلی بازگردید و پیام جدیدی برای فرد دیگری ارسال
            کنید
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/message-sending-interface')}
          className="inline-flex items-center justify-center gap-2 mx-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold transition-all"
        >
          بازگشت به صفحه اصلی
          <Icon name="ArrowRight" size={18} />
        </button>
      </div>
    </div>
  );
};

export default FollowupStatusCard;
